import { CardioOrResistance, EquipmentRequiringInstruction, SmallBodyGroupType } from 'fitbeat.models';
import {
  Exercise,
  ExerciseFormat,
  WorkoutSettings,
} from '../../entities';
import { roundWithinLimits } from '../../helpers/math';
import { HomeBandsUpperLimitMap, homeEquipmentWeightInfoMapping,
  StudioBandUpperLimitMap, studioEquipmentWeightInfoMapping } from './equimpentWeightMaps';
import {
  EquipmentWeightCalculator,
  IResistanceStrategy,
  IUserInfoForEquipmentWeight,
  IWeightAndColor,
  ratioAdjustmentFactorForBands,
} from './EquipmentWeightCalculator';

export class BandResistanceStrategy implements IResistanceStrategy {
  private equipmentWeightInfoMap = homeEquipmentWeightInfoMapping;
  private bandUpperLimit: number;
  private ratioResistanceEquation: (resistance: number, ratio: number) => number;

  constructor(private isHomeGym: boolean, private primaryEquipment: EquipmentRequiringInstruction) {
    this.equipmentWeightInfoMap = this.isHomeGym
      ? homeEquipmentWeightInfoMapping
      : studioEquipmentWeightInfoMapping;
    const bandUpperLimitMap = isHomeGym
      ? HomeBandsUpperLimitMap
      : StudioBandUpperLimitMap;
    const bandUpperLimit = bandUpperLimitMap.get(this.primaryEquipment);
    if (!bandUpperLimit) {
      throw new Error(`No upper limit defined for ${this.primaryEquipment}`);
    }
    this.bandUpperLimit = bandUpperLimit;
    this.ratioResistanceEquation = (resistance: number, ratio: number) => {
      return resistance + (ratio - 1) * ratioAdjustmentFactorForBands;
    };
  }
  public calculateUserStrengthRatio(
    userWeight: number,
    exerciseWeight: number,
  ) {
    return (userWeight - exerciseWeight)/ratioAdjustmentFactorForBands + 1;
  }
  public getEquipmentWeightAdjustedForCardioOrResistance(
    equipment: EquipmentRequiringInstruction,
    uniqueReferenceNumber: string,
    exerciseResistance: number,
    exerciseFormats: ExerciseFormat[],
    primaryMover: SmallBodyGroupType,
    settings: WorkoutSettings,
    user: IUserInfoForEquipmentWeight,
  ) {
    const equipmentWeightMap = this.equipmentWeightInfoMap.get(equipment);
    if (!equipmentWeightMap) {
      throw new Error(`No mapping for, ${equipment}`);
    }
    const adjustedExerciseResistance = this.getAdjustedExerciseResistanceForCardioOrResistance(
      exerciseResistance,
      exerciseFormats,
      primaryMover,
      uniqueReferenceNumber,
      settings,
      user,
    );
    const equipmentWeight = equipmentWeightMap.get(adjustedExerciseResistance);
    if (!equipmentWeight) {
      throw new Error(
        `Could not get equipment weight from adjusted resistance for
        ${equipment} and exercise ${uniqueReferenceNumber}`,
      );
    }
    return {equipmentWeight, adjustedExerciseResistance};
  }
  public getEquipmentWeight(
    equipment: EquipmentRequiringInstruction,
    uniqueReferenceNumber: string,
    exerciseResistance: number,
    primaryMover: SmallBodyGroupType,
    user: IUserInfoForEquipmentWeight,
  ): IWeightAndColor {
    const equipmentWeightMap = this.equipmentWeightInfoMap.get(equipment);
    if (!equipmentWeightMap) {
      throw new Error(`No mapping for, ${equipment}`);
    }
    const strengthResistanceForUser = EquipmentWeightCalculator.getStrengthResistanceForUser(
      exerciseResistance,
      primaryMover,
      user,
      uniqueReferenceNumber,
      this.ratioResistanceEquation,
    );
    const roundedValue = roundWithinLimits(
      strengthResistanceForUser,
      1,
      this.bandUpperLimit,
    );
    const equipmentWeight = equipmentWeightMap.get(roundedValue);
    if (!equipmentWeight) {
      throw new Error(
        `Could not get equipment weight from adjusted resistance for
        ${equipment} and exercise ${uniqueReferenceNumber}`,
      );
    }
    return equipmentWeight;
  }
  private getAdjustedExerciseResistanceForCardioOrResistance(
    exerciseResistance: number,
    exerciseFormats: ExerciseFormat[],
    primaryMover: SmallBodyGroupType,
    uniqueReferenceNumber: string,
    settings: WorkoutSettings,
    user: IUserInfoForEquipmentWeight,
  ) {
    let adjustedStrengthValue = EquipmentWeightCalculator.getStrengthResistanceForUser(
      exerciseResistance,
      primaryMover,
      user,
      uniqueReferenceNumber,
      this.ratioResistanceEquation,
    );
    const shouldLowerWeight =
      Exercise.hasBuildAndBurnFormats(exerciseFormats) &&
      settings.cardioOrResistance === CardioOrResistance.Cardio;
    if (shouldLowerWeight) {
      adjustedStrengthValue = adjustedStrengthValue - 1;
    }
    const adjustedExerciseResistance = roundWithinLimits(
      adjustedStrengthValue,
      1,
      this.bandUpperLimit,
    );
    return adjustedExerciseResistance;
  }
}
